import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Columns3, Plus, Table2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	CrmLoadError,
	CrmPageHeader,
	CrmRoleNotice,
} from "@/components/crm/crm-page";
import { OpportunityFormDialog } from "@/components/crm/opportunity-form";
import {
	OwnerFilter,
	type OwnerFilterValue,
	resolveOwnerFilter,
} from "@/components/crm/owner-select";
import { PipelineKanban } from "@/components/crm/pipeline-kanban";
import { PipelineTable } from "@/components/crm/pipeline-table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/hooks/auth-hooks";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { formatNumber } from "@/lib/crm/format";
import { CRM_STALE_TIME } from "@/lib/crm/queries";
import { CRM_TABS_LIST } from "@/lib/crm/tabs";
import { cn } from "@/lib/utils";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/opportunities/")({
	component: PipelinePage,
});

/** Closed deals older than this drop off the board (they stay in reports and lists). */
const CLOSED_WITHIN_DAYS = 90;
const VIEW_STORAGE_KEY = "admin-crm-pipeline-view";

type View = "board" | "table";

/** Like the CRM tabs: the chosen view in brand colours. */
const VIEW_OPTION =
	"h-8 text-muted-foreground hover:text-foreground aria-checked:bg-primary aria-checked:text-primary-foreground aria-checked:hover:bg-primary aria-checked:hover:text-primary-foreground";

const readView = (): View => {
	try {
		return localStorage.getItem(VIEW_STORAGE_KEY) === "table"
			? "table"
			: "board";
	} catch {
		return "board";
	}
};

function PipelinePage(): ReactElement {
	const { t, i18n } = useTranslation();
	const { canManage } = useCrmPermissions();
	const { data: session } = useSession();
	const [owner, setOwner] = useState<OwnerFilterValue>("all");
	const [view, setView] = useState<View>(readView);
	const [newDealOpen, setNewDealOpen] = useState(false);
	const ownerId = resolveOwnerFilter(owner, session?.user.id);

	const { data, isLoading, isError } = useQuery({
		...orpc.admin.crm.opportunities.board.queryOptions({
			input: { ownerId, closedWithinDays: CLOSED_WITHIN_DAYS },
		}),
		enabled: canManage,
		staleTime: CRM_STALE_TIME,
	});

	if (!canManage) return <CrmRoleNotice />;

	const switchView = (next: View): void => {
		setView(next);
		try {
			localStorage.setItem(VIEW_STORAGE_KEY, next);
		} catch {
			// Storage blocked: the choice just is not remembered.
		}
	};

	const openColumns = (data?.columns ?? []).filter(
		(column) => column.stage !== "closed_won" && column.stage !== "closed_lost",
	);
	const openCount = openColumns.reduce((sum, column) => sum + column.count, 0);
	const openUsers = openColumns.reduce(
		(sum, column) => sum + column.potentialUsers,
		0,
	);

	return (
		<div className="space-y-4">
			<CrmPageHeader
				title={t("admin:crm.pipeline.title")}
				subtitle={
					data
						? t("admin:crm.pipeline.summary", {
								count: openCount,
								users: formatNumber(openUsers, i18n.language),
							})
						: t("admin:crm.pipeline.subtitle")
				}
				actions={
					<Button
						onClick={() => setNewDealOpen(true)}
						data-testid="new-deal-button"
					>
						<Plus className="h-4 w-4" />
						{t("admin:crm.pipeline.new_deal")}
					</Button>
				}
			/>
			<div className="flex flex-wrap items-center gap-2">
				<OwnerFilter value={owner} onChange={setOwner} />
				<div
					className={cn(
						"ml-auto flex items-center gap-0.5 rounded-md p-1",
						CRM_TABS_LIST,
					)}
					role="radiogroup"
					aria-label={t("admin:crm.pipeline.view")}
				>
					<Button
						variant="ghost"
						size="sm"
						role="radio"
						aria-checked={view === "board"}
						className={VIEW_OPTION}
						onClick={() => switchView("board")}
					>
						<Columns3 className="h-4 w-4" />
						{t("admin:crm.pipeline.board")}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						role="radio"
						aria-checked={view === "table"}
						className={VIEW_OPTION}
						onClick={() => switchView("table")}
					>
						<Table2 className="h-4 w-4" />
						{t("admin:crm.pipeline.table")}
					</Button>
				</div>
			</div>
			{isLoading ? (
				<div className="flex gap-3">
					{[0, 1, 2, 3].map((index) => (
						<Skeleton key={index} className="h-64 w-72" />
					))}
				</div>
			) : isError || !data ? (
				<CrmLoadError />
			) : view === "board" ? (
				<PipelineKanban columns={data.columns} canManage={canManage} />
			) : (
				<PipelineTable
					opportunities={data.columns.flatMap((column) => column.opportunities)}
					canManage={canManage}
				/>
			)}
			<OpportunityFormDialog open={newDealOpen} onOpenChange={setNewDealOpen} />
		</div>
	);
}
